import React, { useMemo } from 'react';
import './UniversityFunnels.css';

interface FunnelValue {
  metric: string;
  label: string;
  value: number;
}

interface FunnelCourse {
  course: string;
  label: string;
  values: FunnelValue[];
}

interface CourseInfo {
  course: string;
  label: string;
}

interface Scope {
  kind: string;
  label: string;
}

export interface UniversityFunnelsData {
  type: 'university-funnels';
  title: string;
  subtitle?: string;
  courses: CourseInfo[];
  funnels: FunnelCourse[];
  scope?: Scope;
  question?: string;
}

interface SegmentInfo {
  key: string;
  label: string;
  color: string;
}

interface StageData {
  stageLabel: string;
  segments: Record<string, number>;
  currentTotal: number;
  referenceTotal: number;
  changeValue: number;
  changeDirection: 'up' | 'down' | 'neutral';
}

const SEGMENT_COLORS = [
  '#5b8fb9',
  '#7be495',
  '#f6c85f',
  '#c490d1',
  '#6dd3e3',
  '#f87171',
  '#fb923c',
  '#a3e635',
];

/**
 * Tries to detect if a metric follows a pattern like "Stage_Segment" (e.g. "Solicitudes_Internacionales").
 * If a separator is found and multiple metrics share the same prefix, we group them.
 * Otherwise each metric is its own stage with no sub-segments.
 */
function analyzeMetrics(values: FunnelValue[]): {
  stages: string[];
  segments: SegmentInfo[];
  metricToStage: Record<string, string>;
  metricToSegment: Record<string, string>;
} {
  const separators = ['_', ' - ', ' '];
  let bestSep: string | null = null;
  let bestPrefixes: Map<string, string[]> | null = null;

  for (const sep of separators) {
    const prefixMap = new Map<string, string[]>();
    let allHaveSep = true;

    for (const v of values) {
      const sepIdx = v.metric.indexOf(sep);
      if (sepIdx <= 0) {
        allHaveSep = false;
        break;
      }
      const prefix = v.metric.substring(0, sepIdx);
      const existing = prefixMap.get(prefix) || [];
      existing.push(v.metric);
      prefixMap.set(prefix, existing);
    }

    if (allHaveSep && prefixMap.size < values.length) {
      bestSep = sep;
      bestPrefixes = prefixMap;
      break;
    }
  }

  if (bestSep && bestPrefixes) {
    const stages: string[] = [];
    const segmentSet = new Set<string>();
    const metricToStage: Record<string, string> = {};
    const metricToSegment: Record<string, string> = {};

    for (const v of values) {
      const sepIdx = v.metric.indexOf(bestSep);
      const prefix = v.metric.substring(0, sepIdx);
      const suffix = v.metric.substring(sepIdx + bestSep.length);

      const stageLabel = prefix.replace(/_/g, ' ');
      if (!stages.includes(stageLabel)) stages.push(stageLabel);
      segmentSet.add(suffix);
      metricToStage[v.metric] = stageLabel;
      metricToSegment[v.metric] = suffix;
    }

  const segments: SegmentInfo[] = Array.from(segmentSet).map((seg, idx) => ({
    key: seg,
    label: seg.replace(/_/g, ' '),
    color: SEGMENT_COLORS[idx % SEGMENT_COLORS.length],
  }));

  if (segments.length === 1) {
    segments[0].color = '#7be495';
  }

  return { stages, segments, metricToStage, metricToSegment };
  }

  const stages: string[] = [];
  const metricToStage: Record<string, string> = {};
  const metricToSegment: Record<string, string> = {};

  for (const v of values) {
    const stageLabel = v.label || v.metric.replace(/_/g, ' ');
    stages.push(stageLabel);
    metricToStage[v.metric] = stageLabel;
    metricToSegment[v.metric] = '__single__';
  }

  const segments: SegmentInfo[] = [{ key: '__single__', label: '', color: '#7be495' }];
  return { stages, segments, metricToStage, metricToSegment };
}

const UniversityFunnels: React.FC<{ data: UniversityFunnelsData }> = ({ data }) => {
  const { title, subtitle, courses, funnels } = data;

  const currentCourse = courses.length > 1 ? courses[courses.length - 1] : courses[0];
  const referenceCourse = courses.length > 1 ? courses[0] : null;

  const currentFunnel = funnels.find(f => f.course === currentCourse.course);
  const referenceFunnel = referenceCourse
    ? funnels.find(f => f.course === referenceCourse.course)
    : null;

  const analysis = useMemo(() => {
    if (!currentFunnel) return null;
    return analyzeMetrics(currentFunnel.values);
  }, [currentFunnel]);

  const stages: StageData[] = useMemo(() => {
    if (!currentFunnel || !analysis) return [];

    const { stages: stageOrder, metricToStage, metricToSegment } = analysis;

    const stageMap: Record<string, Record<string, number>> = {};
    for (const stage of stageOrder) {
      stageMap[stage] = {};
    }

    for (const v of currentFunnel.values) {
      const stage = metricToStage[v.metric];
      const segment = metricToSegment[v.metric];
      if (!stageMap[stage]) stageMap[stage] = {};
      stageMap[stage][segment] = (stageMap[stage][segment] || 0) + v.value;
    }

    const refMap: Record<string, number> = {};
    if (referenceFunnel) {
      const refAnalysis = analyzeMetrics(referenceFunnel.values);
      for (const v of referenceFunnel.values) {
        const stage = refAnalysis.metricToStage[v.metric];
        refMap[stage] = (refMap[stage] || 0) + v.value;
      }
    }

    return stageOrder.map(stageLabel => {
      const segments = stageMap[stageLabel] || {};
      const currentTotal = Object.values(segments).reduce((sum, val) => sum + val, 0);
      const referenceTotal = refMap[stageLabel] || 0;

      let changeValue = 0;
      let changeDirection: 'up' | 'down' | 'neutral' = 'neutral';
      if (referenceTotal > 0) {
        changeValue = ((currentTotal - referenceTotal) / referenceTotal) * 100;
        changeDirection = changeValue > 0 ? 'up' : changeValue < 0 ? 'down' : 'neutral';
      }

      return {
        stageLabel,
        segments,
        currentTotal,
        referenceTotal,
        changeValue,
        changeDirection,
      };
    });
  }, [currentFunnel, referenceFunnel, analysis]);

  const maxTotal = useMemo(() => Math.max(...stages.map(s => s.currentTotal), 1), [stages]);

  const visibleSegments = analysis?.segments.filter(seg => seg.label) || [];
  const isSingleSegment = analysis?.segments.length === 1;

  const singleSegmentLabel = useMemo(() => {
    if (!isSingleSegment || !currentFunnel) return '';
    const metrics = currentFunnel.values.map(v => v.metric);
    const suffixes = metrics.map(m => {
      const lastUnderscore = m.lastIndexOf('_');
      return lastUnderscore > 0 ? m.substring(lastUnderscore + 1) : '';
    });
    const commonSuffix = suffixes.every(s => s === suffixes[0]) ? suffixes[0] : '';
    if (commonSuffix) return commonSuffix.replace(/_/g, ' ');
    const labels = currentFunnel.values.map(v => v.label);
    const words = labels[0]?.split(' ') || [];
    const shared = words.filter(w => labels.every(l => l.toLowerCase().includes(w.toLowerCase())));
    return shared.join(' ') || '';
  }, [isSingleSegment, currentFunnel]);

  return (
    <div className="university-funnels">
      <div className="uf-header">
        <span className="uf-label">EMBUDO DE ADMISIÓN</span>
        <h2 className="uf-title">
          {data.scope?.label || title}
          <span className="uf-course-badge">{currentCourse.label}</span>
        </h2>
        {subtitle && <p className="uf-subtitle">{subtitle}</p>}
      </div>

      {(visibleSegments.length > 0 || singleSegmentLabel || referenceCourse) && (
        <div className="uf-legend">
          {visibleSegments.length > 0 ? (
            visibleSegments.map(seg => (
              <span className="uf-legend-item" key={seg.key}>
                <span className="uf-legend-swatch" style={{ background: seg.color }} />
                {seg.label}
              </span>
            ))
          ) : singleSegmentLabel ? (
            <span className="uf-legend-item">
              <span className="uf-legend-swatch" style={{ background: '#7be495' }} />
              {singleSegmentLabel}
            </span>
          ) : null}
          {referenceCourse && (
            <span className="uf-legend-item">
              <span className="uf-legend-swatch uf-swatch-reference" />
              {referenceCourse.label} (referencia)
            </span>
          )}
        </div>
      )}

      <div className="uf-bars">
        {stages.map((stage) => {
          const heightPct = (stage.currentTotal / maxTotal) * 100;
          const refHeightPct = referenceFunnel
            ? (stage.referenceTotal / maxTotal) * 100
            : 0;

          const segmentEntries = analysis?.segments || [];
          const segmentPcts = segmentEntries.map(seg => {
            const val = stage.segments[seg.key] || 0;
            return {
              ...seg,
              value: val,
              pct: stage.currentTotal > 0 ? (val / stage.currentTotal) * 100 : 0,
            };
          });

          return (
            <div className="uf-bar-column" key={stage.stageLabel}>
              {!isSingleSegment && segmentEntries.length === 2 && (
                <div className="uf-intl-pct">
                  {segmentPcts[1]?.pct.toFixed(1)} %
                </div>
              )}
              <div className="uf-bar-wrapper">
                <div
                  className="uf-bar"
                  style={{ height: `${heightPct}%` }}
                >
                  {isSingleSegment ? (
                    <div
                      className="uf-bar-segment"
                      style={{ height: '100%', background: '#7be495' }}
                    />
                  ) : (
                    segmentPcts.map(seg => (
                      <div
                        className="uf-bar-segment"
                        key={seg.key}
                        style={{
                          height: `${seg.pct}%`,
                          background: seg.color,
                        }}
                      />
                    ))
                  )}
                </div>
                {referenceFunnel && (
                  <div
                    className="uf-bar-reference"
                    style={{ height: `${refHeightPct}%` }}
                  />
                )}
              </div>
              <div className="uf-bar-label">{stage.stageLabel}</div>
              <div className="uf-bar-total">{stage.currentTotal.toLocaleString('es-ES')}</div>
              {referenceCourse && (
                <div className="uf-bar-change">
                  <span className={`uf-change-badge uf-change-${stage.changeDirection}`}>
                    {stage.changeDirection === 'up' ? '▲' : stage.changeDirection === 'down' ? '▼' : ''}
                    {stage.changeValue >= 0 ? '+' : ''}{stage.changeValue.toFixed(1)} %
                  </span>
                  <span className="uf-ref-value">
                    {referenceCourse.label}: <strong>{stage.referenceTotal.toLocaleString('es-ES')}</strong>
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UniversityFunnels;
